import type { Schema } from "@/shared/components/Form/types";
import type {
  CreateSchemaDefinition,
  EditSchemaDefinition,
} from "@/shared/components/CRUD/types";
import {
  CreateDocumentMutation,
  UpdateDocumentMutation,
  DocumentEditQuery,
  DocumentShowQuery,
  type UpdateDocumentInput,
} from "@/admin/entities/documents";
import type {
  DocumentEditQueryQueryType,
  DocumentShowQueryQueryType,
} from "@/types/graphql/admin.generated";
import { DocumentTypeType } from "@/admin/entities/documents";
import { PlatformsAutocompleteQuery } from "@/admin/entities/platforms";
import { LanguagesAutocompleteQuery } from "@/admin/entities/languages";

/**
 * Схемы форм для feature управления документами
 * Используют entity мутации и запросы
 */

// Фильтры списка документов
export const filterSchema: Schema = {
  search: {
    label: "Поиск",
    type: "text",
    placeholder: "Поиск по названию...",
    xs: 12,
    md: 6,
    fullWidth: true,
    priority: "primary",
  },
  platformId: {
    label: "Платформа",
    type: "autocomplete",
    query: PlatformsAutocompleteQuery,
    xs: 12,
    md: 6,
    fullWidth: true,
    priority: "secondary",
  },
  languageId: {
    label: "Язык",
    type: "autocomplete",
    query: LanguagesAutocompleteQuery,
    xs: 12,
    md: 6,
    fullWidth: true,
    priority: "secondary",
  },
  type: {
    label: "Тип",
    type: "select",
    priority: "quick",
    xs: 12,
    md: 6,
    fullWidth: true,
    options: [
      { label: "Все типы", value: "" },
      { label: "OTT (с шаблонами)", value: DocumentTypeType.OttType },
    ],
  },
};

// Схема создания документа
export const createSchema: CreateSchemaDefinition = {
  main: {
    label: "Создание документа",
    schema: {
      title: {
        required: true,
        label: "Название",
        helperText:
          "Название документа. Можно использовать {{PLATFORM_TITLE}} и другие переменные.",
        type: "text",
        xs: 12,
        fullWidth: true,
      },
      slug: {
        required: true,
        label: "Slug",
        helperText:
          "URL путь документа: terms-of-service, privacy-policy, refund-policy. Только строчные буквы, цифры и дефисы.",
        type: "text",
        xs: 12,
        md: 6,
        fullWidth: true,
      },
      type: {
        required: true,
        label: "Тип",
        type: "select",
        xs: 12,
        md: 6,
        fullWidth: true,
        options: [
          { label: "OTT (с шаблонами)", value: DocumentTypeType.OttType },
        ],
      },
      platformId: {
        label: "Платформа",
        helperText: "Оставьте пустым для глобального документа",
        type: "autocomplete",
        query: PlatformsAutocompleteQuery,
        xs: 12,
        md: 6,
        fullWidth: true,
      },
      languageId: {
        label: "Язык",
        helperText:
          "Оставьте пустым для language-agnostic документа (резолвится для любого языка платформы, если нет более точной версии)",
        type: "autocomplete",
        query: LanguagesAutocompleteQuery,
        xs: 12,
        md: 6,
        fullWidth: true,
      },
      text: {
        label: "Содержимое (Markdown)",
        helperText:
          "Поддерживается Markdown и переменные: {{PLATFORM_TITLE}}, {{PLATFORM_URL}}, {{PLATFORM_SUPPORT_EMAIL}}, {{PLATFORM_PRIVACY_POLICY_URL}}, {{PLATFORM_TERMS_OF_SERVICE_URL}}, {{PLATFORM_REFUND_POLICY_URL}}",
        type: "markdown",
        required: true,
        xs: 12,
        rows: 15,
        fullWidth: true,
      },
      isPublished: {
        label: "Опубликовано",
        type: "checkbox",
        xs: 12,
        md: 6,
      },
      date: {
        label: "Дата вступления в силу",
        helperText: "Дата, когда документ вступает в силу",
        type: "date",
        xs: 12,
        md: 6,
      },
    },
    createMutation: CreateDocumentMutation,
  },
} as const;

// Схема редактирования документа
export const editSchema: EditSchemaDefinition = {
  details: {
    label: "Детали",
    type: "show" as const,
    getTitle: (data: unknown) => {
      const { document } = data as DocumentShowQueryQueryType;
      if (!document) return null;
      return document.title;
    },
    showQuery: DocumentShowQuery,
  },
  main: {
    label: "Редактировать",
    type: "form" as const,
    updateMutation: UpdateDocumentMutation,
    editQuery: DocumentEditQuery,
    schema: {
      title: {
        required: true,
        label: "Название",
        helperText: "Название документа (поддерживает переменные)",
        type: "text",
        xs: 12,
        fullWidth: true,
      },
      slug: {
        required: true,
        label: "Slug",
        helperText: "URL путь документа",
        type: "text",
        xs: 12,
        md: 6,
        fullWidth: true,
      },
      type: {
        required: true,
        label: "Тип",
        type: "select",
        xs: 12,
        md: 6,
        fullWidth: true,
        options: [
          { label: "OTT (с шаблонами)", value: DocumentTypeType.OttType },
        ],
      },
      platformId: {
        label: "Платформа",
        helperText: "Оставьте пустым для глобального документа",
        type: "autocomplete",
        query: PlatformsAutocompleteQuery,
        xs: 12,
        md: 6,
        fullWidth: true,
      },
      languageId: {
        label: "Язык",
        helperText:
          "Оставьте пустым для language-agnostic документа (резолвится для любого языка платформы, если нет более точной версии)",
        type: "autocomplete",
        query: LanguagesAutocompleteQuery,
        xs: 12,
        md: 6,
        fullWidth: true,
      },
      text: {
        label: "Содержимое (Markdown)",
        helperText: "Поддерживается Markdown и переменные",
        type: "markdown",
        xs: 12,
        rows: 15,
        fullWidth: true,
      },
      isPublished: {
        label: "Опубликовано",
        type: "checkbox",
        xs: 12,
        md: 4,
      },
      date: {
        label: "Дата вступления в силу",
        type: "date",
        xs: 12,
        md: 4,
      },
      isNewVersion: {
        label: "Создать новую версию",
        helperText:
          "Отметьте чтобы создать новую версию документа. Текущая версия будет архивирована.",
        type: "checkbox",
        xs: 12,
        md: 4,
      },
    },
    getTitle: (data: unknown) => {
      const { document } = data as DocumentShowQueryQueryType;
      if (!document) return null;
      return document.title;
    },
    getEditData: (data: unknown): UpdateDocumentInput => {
      // Returns UpdateDocumentInput with __typename removed
      const { document } = data as DocumentEditQueryQueryType;
      return {
        title: document.title,
        text: document.text,
        slug: document.slug,
        type: document.type,
        platformId: document.platform?.id || null,
        languageId: document.language?.id || null,
        isPublished: document.isPublished,
        date: document.date,
      };
    },
  },
} as const;
